import { useState, useEffect } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";

const API = process.env.REACT_APP_API_URL || "";

const TYPE_COLORS = {
  quotes:         { color: "#002DB5", bg: "#DBEAFE" },
  consultations:  { color: "#065F46", bg: "#D1FAE5" },
  contacts:       { color: "#6D28D9", bg: "#EDE9FE" },
  serviceRequests:{ color: "#B45309", bg: "#FEF3C7" },
};

const INSURANCE_COLORS = [
  "#002DB5","#065F46","#6D28D9","#B45309","#0F766E","#BE123C","#1D4ED8","#92400E",
];

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.3)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
      </svg>
    </div>
  );
}

// ── SVG Bar Chart ─────────────────────────────────────────────────────
function BarChart({ data, color = "#002DB5", height = 160 }) {
  if (!data || data.length === 0) return <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic", padding: "24px 0" }}>No data yet.</p>;
  const max  = Math.max(...data.map(d => d.value), 1);
  const barW = Math.max(8, Math.min(32, Math.floor(560 / data.length) - 4));
  const gap  = 4;
  const totalW = data.length * (barW + gap);

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={Math.max(totalW, 200)} height={height + 32} style={{ display: "block" }}>
        {data.map((d, i) => {
          const barH = Math.max(2, Math.round((d.value / max) * height));
          const x = i * (barW + gap);
          const y = height - barH;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} rx="4"
                fill={color} opacity="0.85">
                <title>{d.label}: {d.value}</title>
              </rect>
              {d.value > 0 && (
                <text x={x + barW / 2} y={y - 4} textAnchor="middle"
                  style={{ fontFamily: "Arial,sans-serif", fontSize: "9px", fill: color, fontWeight: 600 }}>
                  {d.value}
                </text>
              )}
              <text x={x + barW / 2} y={height + 16} textAnchor="middle"
                style={{ fontFamily: "Arial,sans-serif", fontSize: "8px", fill: "rgba(1,46,114,0.4)" }}>
                {d.shortLabel || d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── SVG Line Chart (multi-series) ─────────────────────────────────────
function LineChart({ timeline, height = 180 }) {
  if (!timeline || timeline.length === 0) return <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic", padding: "24px 0" }}>No data yet.</p>;

  const series = [
    { key: "quotes",          color: "#002DB5", label: "Quotes" },
    { key: "consultations",   color: "#065F46", label: "Consultations" },
    { key: "contacts",        color: "#6D28D9", label: "Messages" },
    { key: "serviceRequests", color: "#B45309", label: "Service Requests" },
  ];

  const allVals = timeline.flatMap(d => series.map(s => d[s.key] || 0));
  const max  = Math.max(...allVals, 1);
  const w    = 560;
  const step = timeline.length > 1 ? w / (timeline.length - 1) : w;

  const toY = (v) => height - Math.round((v / max) * (height - 10)) - 4;
  const toX = (i) => Math.round(i * step);

  const pointsFor = (key) => timeline.map((d, i) => `${toX(i)},${toY(d[key] || 0)}`).join(" ");

  // Show every Nth label to avoid crowding
  const labelStep = Math.max(1, Math.ceil(timeline.length / 8));

  return (
    <div style={{ overflowX: "auto" }}>
      <svg width={w} height={height + 28} style={{ display: "block" }}>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map(pct => {
          const y = height - Math.round(pct * (height - 10)) - 4;
          return (
            <g key={pct}>
              <line x1={0} y1={y} x2={w} y2={y} stroke="rgba(1,46,114,0.06)" strokeWidth="1"/>
              <text x={0} y={y - 2} style={{ fontFamily: "Arial,sans-serif", fontSize: "8px", fill: "rgba(1,46,114,0.3)" }}>
                {Math.round(max * pct)}
              </text>
            </g>
          );
        })}

        {/* Lines */}
        {series.map(s => (
          <polyline key={s.key} points={pointsFor(s.key)}
            fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" opacity="0.85"/>
        ))}

        {/* X-axis labels */}
        {timeline.map((d, i) => {
          if (i % labelStep !== 0 && i !== timeline.length - 1) return null;
          const label = d.date.slice(5); // "MM-DD"
          return (
            <text key={i} x={toX(i)} y={height + 18} textAnchor="middle"
              style={{ fontFamily: "Arial,sans-serif", fontSize: "8px", fill: "rgba(1,46,114,0.4)" }}>
              {label}
            </text>
          );
        })}
      </svg>

      {/* Legend */}
      <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "10px" }}>
        {series.map(s => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div style={{ width: "12px", height: "3px", borderRadius: "2px", background: s.color }} />
            <span style={{ fontFamily: "Arial,sans-serif", fontSize: "10px", color: "rgba(1,46,114,0.5)" }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Donut chart ───────────────────────────────────────────────────────
function DonutChart({ slices, size = 120 }) {
  if (!slices || slices.length === 0 || slices.every(s => s.value === 0))
    return <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>No data yet.</p>;

  const total = slices.reduce((a, s) => a + s.value, 0);
  const r = 46; const cx = size / 2; const cy = size / 2;
  let cum = 0;
  const paths = slices.map(s => {
    const pct = s.value / total;
    const start = cum * 2 * Math.PI - Math.PI / 2;
    cum += pct;
    const end = cum * 2 * Math.PI - Math.PI / 2;
    const large = pct > 0.5 ? 1 : 0;
    const x1 = cx + r * Math.cos(start); const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);   const y2 = cy + r * Math.sin(end);
    return { ...s, d: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z` };
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
      <svg width={size} height={size}>
        {paths.map((p, i) => <path key={i} d={p.d} fill={p.color} opacity="0.88"><title>{p.label}: {p.value}</title></path>)}
        <circle cx={cx} cy={cy} r={28} fill="#fff"/>
        <text x={cx} y={cy + 4} textAnchor="middle" style={{ fontFamily: "Arial,sans-serif", fontSize: "11px", fontWeight: 700, fill: "#012E72" }}>{total}</text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: "10px", height: "10px", borderRadius: "3px", background: s.color, flexShrink: 0 }} />
            <span style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#012E72" }}>{s.label}</span>
            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "11px", fontWeight: 700, color: s.color }}>{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Card wrapper ──────────────────────────────────────────────────────
function Card({ title, subtitle, children }) {
  return (
    <div style={{ background: "#fff", borderRadius: "16px", padding: "24px 28px", border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 16px rgba(1,46,114,0.06)" }}>
      <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: subtitle ? "4px" : "20px" }}>{title}</p>
      {subtitle && <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.4)", marginBottom: "20px", fontStyle: "italic" }}>{subtitle}</p>}
      {children}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────
export default function AdminAnalytics() {
  usePageTitle("Analytics");
  const [range,   setRange]   = useState(30);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminFetch(`${API}/api/admin/analytics?range=${range}`)
      .then(r => r.json())
      .then(j => { if (j.success) setData(j); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [range]);

  const totals        = data?.totals        || {};
  const byStatus      = data?.byStatus      || {};
  const quotesByType  = data?.quotesByType  || [];
  const timeline      = data?.timeline      || [];

  const grandTotal = Object.values(totals).reduce((a, v) => a + v, 0);

  // Quote status slices
  const quoteStatusSlices = [
    { label: "Pending",  value: byStatus.quotes?.pending  || 0, color: "#FCD34D" },
    { label: "Reviewed", value: byStatus.quotes?.reviewed || 0, color: "#60A5FA" },
    { label: "Quoted",   value: byStatus.quotes?.quoted   || 0, color: "#34D399" },
    { label: "Closed",   value: byStatus.quotes?.closed   || 0, color: "#D1D5DB" },
  ].filter(s => s.value > 0);

  // Consultation status slices
  const consultSlices = [
    { label: "Pending",   value: byStatus.consultations?.pending   || 0, color: "#FCD34D" },
    { label: "Contacted", value: byStatus.consultations?.contacted || 0, color: "#60A5FA" },
    { label: "Completed", value: byStatus.consultations?.completed || 0, color: "#34D399" },
  ].filter(s => s.value > 0);

  // Submission breakdown bar
  const submissionBar = [
    { label: "Quotes",           shortLabel: "Quotes",    value: totals.quotes         || 0, color: TYPE_COLORS.quotes.color },
    { label: "Consultations",    shortLabel: "Consults",  value: totals.consultations  || 0, color: TYPE_COLORS.consultations.color },
    { label: "Messages",         shortLabel: "Messages",  value: totals.contacts       || 0, color: TYPE_COLORS.contacts.color },
    { label: "Service Requests", shortLabel: "Services",  value: totals.serviceRequests|| 0, color: TYPE_COLORS.serviceRequests.color },
  ];

  // Insurance type bars
  const typeBar = quotesByType.map((t, i) => ({
    label: t._id.charAt(0).toUpperCase() + t._id.slice(1),
    shortLabel: t._id.length > 7 ? t._id.slice(0, 6) + "…" : t._id,
    value: t.count,
    color: INSURANCE_COLORS[i % INSURANCE_COLORS.length],
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px" }}>Admin / Analytics</p>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.04em", color: "#012E72", lineHeight: 1 }}>Analytics</h1>
        </div>
        {/* Range selector */}
        <div style={{ display: "flex", gap: "6px" }}>
          {[{ v: 30, l: "30 days" }, { v: 90, l: "90 days" }, { v: 365, l: "1 year" }].map(r => (
            <button key={r.v} onClick={() => setRange(r.v)} style={{
              padding: "8px 18px", borderRadius: "999px", cursor: "pointer", transition: "all 0.15s",
              fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase",
              background: range === r.v ? "#012E72" : "#fff",
              color:      range === r.v ? "#fff"    : "#6B7280",
              border: `1px solid ${range === r.v ? "#012E72" : "rgba(1,46,114,0.12)"}`,
            }}>{r.l}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {/* ── KPI row ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "14px" }}>
            {[
              { label: "Total Submissions", value: grandTotal,              color: "#012E72", bg: "rgba(1,46,114,0.07)" },
              { label: "Quote Requests",    value: totals.quotes || 0,      ...TYPE_COLORS.quotes },
              { label: "Consultations",     value: totals.consultations||0, ...TYPE_COLORS.consultations },
              { label: "Messages",          value: totals.contacts || 0,    ...TYPE_COLORS.contacts },
              { label: "Service Requests",  value: totals.serviceRequests||0,...TYPE_COLORS.serviceRequests },
            ].map(k => (
              <div key={k.label} style={{ background: k.bg, borderRadius: "14px", padding: "18px 20px" }}>
                <p style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "28px", color: k.color, margin: "0 0 4px" }}>{k.value}</p>
                <p style={{ fontFamily: "'Cinzel', serif", fontSize: "7px", letterSpacing: "0.16em", textTransform: "uppercase", color: k.color, opacity: 0.7, margin: 0 }}>{k.label}</p>
              </div>
            ))}
          </div>

          {/* ── Timeline ── */}
          <Card title={`Submissions Over Time — Last ${range} Days`} subtitle="All channels combined">
            <LineChart timeline={timeline} />
          </Card>

          {/* ── Submission breakdown + Quote status ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <Card title="Total by Channel" subtitle="All time">
              <BarChart
                data={submissionBar.map(d => ({ ...d }))}
                color="#002DB5"
              />
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "12px" }}>
                {submissionBar.map(d => (
                  <div key={d.label} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div style={{ width: "10px", height: "10px", borderRadius: "3px", background: d.color }} />
                    <span style={{ fontFamily: "Arial,sans-serif", fontSize: "10px", color: "rgba(1,46,114,0.5)" }}>{d.label}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card title="Quote Status Breakdown" subtitle="All time">
              <DonutChart slices={quoteStatusSlices} />
            </Card>
          </div>

          {/* ── Consultation status + Insurance types ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
            <Card title="Consultation Status" subtitle="All time">
              <DonutChart slices={consultSlices} />
            </Card>

            <Card title="Top Insurance Types Requested" subtitle="From quote submissions">
              {typeBar.length > 0 ? (
                <BarChart data={typeBar} color="#002DB5" height={140} />
              ) : (
                <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>No quote data yet.</p>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
